// The touch HUD — what a phone has instead of a keyboard.
//
// Round 14 gave coarse-pointer devices four action buttons and left walking on tap-to-move. That
// was never enough for a world whose every lesson is a movement verb: stepping off a ledge,
// climbing a face, banking a wing and vaulting a wall all need a DIRECTION, and touch had none
// (and tap-to-move only reaches the NavMesh, which is a bounded box around the room origin).
//
// So: a stick under the left thumb, the four buttons under the right, and the buttons say what
// they will actually do — "jump" on a wall is a lie, there the same button lets go.
import { useCallback, useEffect, useRef, useState } from 'react';
import './TouchActions.css';

/** The body's current verb, pushed by Unity ("move_state"). */
export type MoveState = 'ground' | 'air' | 'climb' | 'glide' | 'fly' | 'slide' | 'crouch';

/** Touch-shaped device? Pointer coarseness, not user agents — a tablet wants the same targets. */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    try {
      const mq = window.matchMedia('(pointer: coarse)');
      // Coarse pointer OR a real touchscreen: the Android emulator's Chrome (and a phone with a
      // mouse paired, or DeX) answers the media query with false while the glass is still glass.
      const touch = () => mq.matches || navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
      setCoarse(touch());
      const onChange = () => setCoarse(touch());
      mq.addEventListener?.('change', onChange);
      return () => mq.removeEventListener?.('change', onChange);
    } catch {
      return undefined;
    }
  }, []);
  return coarse;
}

const STICK_RADIUS = 46; // px of travel from the base centre to full deflection

/**
 * THE LEFT THUMB. Emits a unit vector on the "move_axis" bridge message, in the same space the
 * keyboard's axis lands, so Unity has ONE movement model. How far you push is the speed dial —
 * a nudge strolls, most of the way runs, the rim sprints — which is why there is no run button.
 */
export function TouchStick({
  send,
  side = 'left',
  label = 'Move',
}: {
  send: (x: number, y: number) => void;
  /** Left = the body (move_axis), right = the camera (look_axis). Physical sides: thumbs, not text. */
  side?: 'left' | 'right';
  label?: string;
}) {
  const coarse = useCoarsePointer();
  const baseRef = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);
  const last = useRef({ x: 0, y: 0, t: 0 });
  // A REF, not the knob state: the first pointermove of a drag arrives before React has
  // re-rendered, so a state check drops the opening milliseconds of every push — which is
  // exactly the part a thumb feels. Pointer pressure is not reliable across touch hardware.
  const down = useRef(false);
  // THE HEARTBEAT (operator, 2026-09-08: "static hold on left is returning to center, and when
  // moved it goes left again"). A thumb held still fires no pointermove, so a stick that only
  // speaks on movement goes silent — and any dropped or coalesced message then leaves the world
  // believing the stick is centred until the thumb twitches again. While it is held, the current
  // vector is re-sent ten times a second; the moment it is released, a zero is sent once.
  const vec = useRef({ x: 0, y: 0 });

  const emit = useCallback(
    (x: number, y: number, force = false) => {
      const now = performance.now();
      const moved = Math.abs(x - last.current.x) + Math.abs(y - last.current.y);
      if (!force && now - last.current.t < 50 && moved < 0.06) return; // ~20 Hz, on change only
      last.current = { x, y, t: now };
      send(x, y);
    },
    [send],
  );

  const track = useCallback(
    (clientX: number, clientY: number) => {
      const el = baseRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let dx = (clientX - (r.left + r.width / 2)) / STICK_RADIUS;
      let dy = (clientY - (r.top + r.height / 2)) / STICK_RADIUS;
      const len = Math.hypot(dx, dy);
      if (len > 1) {
        dx /= len;
        dy /= len;
      }
      setKnob({ x: dx * STICK_RADIUS, y: dy * STICK_RADIUS });
      vec.current = { x: dx, y: -dy };
      emit(dx, -dy); // screen-down is world-backward
    },
    [emit],
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      if (down.current) send(vec.current.x, vec.current.y);
    }, 100);
    return () => window.clearInterval(id);
  }, [send]);

  const release = useCallback(() => {
    down.current = false;
    vec.current = { x: 0, y: 0 };
    setKnob(null);
    emit(0, 0, true);
  }, [emit]);

  useEffect(() => () => send(0, 0), [send]); // never leave the body walking on unmount

  if (!coarse) return null;

  return (
    <div
      className={`touch-stick touch-stick--${side}`}
      ref={baseRef}
      role="application"
      aria-label={label}
      onPointerDown={(e) => {
        down.current = true;
        // Capture on the STICK, not the knob under the finger, or the drag ends the moment the
        // thumb slides off that child element.
        e.currentTarget.setPointerCapture?.(e.pointerId);
        track(e.clientX, e.clientY);
      }}
      onPointerMove={(e) => {
        if (!down.current) return;
        track(e.clientX, e.clientY);
      }}
      onPointerUp={release}
      onPointerCancel={release}
      onLostPointerCapture={release}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') release();
      }}
    >
      <span className="touch-stick-ring" aria-hidden />
      <span
        className={`touch-stick-knob${knob ? ' is-held' : ''}`}
        style={knob ? { transform: `translate(${knob.x}px, ${knob.y}px)` } : undefined}
        aria-hidden
      />
    </div>
  );
}

/**
 * THE BUTTONS LIVE ABOVE THE STICKS (phone pass 2: "put buttons above the movement for actions —
 * such that the thumbs can handle this"). Each thumb rests on its stick and lifts to a pair of
 * buttons right above it: the LEFT thumb owns the body (jump, crouch), the RIGHT thumb owns the
 * air (wing, jet) — and each button names what it does in the state the body is actually in.
 * "Jump" on a wall is a lie: there the same button lets go. The wing pulses the moment a fall
 * makes it useful, which is how a child learns a glider exists without being told.
 */
export function TouchActions({ send, state = 'ground' }: { send: (action: string) => void; state?: MoveState }) {
  const coarse = useCoarsePointer();
  const [crouched, setCrouched] = useState(false);
  if (!coarse) return null;

  const climbing = state === 'climb';
  const flying = state === 'glide' || state === 'fly';
  const toggleCrouch = () => {
    const next = !crouched;
    setCrouched(next);
    send(next ? 'crouch_on' : 'crouch_off');
  };

  return (
    <>
      <div className="touch-actions touch-actions--left" aria-label="Body buttons">
        <button type="button" className="touch-btn touch-btn--big"
                onPointerDown={() => send('jump')}
                aria-label={climbing ? 'Let go of the wall' : 'Jump'}>
          {climbing ? '✋' : '⤴️'}
        </button>
        <button type="button"
                className={`touch-btn${crouched ? ' is-on' : ''}`}
                disabled={climbing || flying}
                onPointerDown={toggleCrouch} aria-label="Crouch">
          🤸
        </button>
      </div>
      <div className="touch-actions touch-actions--right" aria-label="Air buttons">
        <button type="button"
                className={`touch-btn touch-btn--big${state === 'air' ? ' is-ready' : ''}`}
                onPointerDown={() => send('glide')}
                aria-label={state === 'glide' ? 'Stow the wing' : 'Glider — open the wing'}>
          {state === 'glide' ? '⤵️' : '🪂'}
        </button>
        <button type="button" className="touch-btn"
                onPointerDown={() => send('jet_on')}
                onPointerUp={() => send('jet_off')}
                onPointerLeave={() => send('jet_off')}
                onPointerCancel={() => send('jet_off')}
                aria-label="Jetpack — hold">
          🚀
        </button>
      </div>
    </>
  );
}

/**
 * THE GREEN GO BUTTON — shown while the place under the player offers a launch (a trampoline).
 * Deliberately NOT touch-gated: a mouse needs the same door, and this is a game button, not a
 * touch control. Leaving the balloon becomes a thing you press when ready.
 */
export function LaunchButton({ show, send }: { show: boolean; send: (action: string) => void }) {
  if (!show) return null;
  return (
    <button type="button" className="touch-go" onPointerDown={() => send('launch')} aria-label="Go — launch">
      <span aria-hidden>🚀</span> GO
    </button>
  );
}

/** Portrait on a phone is a letterbox with thumbs over it. Ask once, in place, and get out of the way. */
export function RotateToLandscape() {
  const coarse = useCoarsePointer();
  const [portrait, setPortrait] = useState(false);
  useEffect(() => {
    try {
      const mq = window.matchMedia('(orientation: portrait)');
      setPortrait(mq.matches);
      const onChange = (e: MediaQueryListEvent) => setPortrait(e.matches);
      mq.addEventListener?.('change', onChange);
      return () => mq.removeEventListener?.('change', onChange);
    } catch {
      return undefined;
    }
  }, []);
  if (!coarse || !portrait) return null;
  return (
    <div className="touch-rotate" role="status">
      <div className="touch-rotate-card">
        <span className="touch-rotate-icon" aria-hidden>📱↻</span>
        <strong>Turn your phone sideways</strong>
        <span>The world plays in landscape — your thumbs need the corners.</span>
      </div>
    </div>
  );
}

const COACH_KEY = 'gamedate.touchCoach.v1';

/** Two bubbles, once per device: what the stick does, what the wing does. Then never again. */
export function TouchCoach() {
  const coarse = useCoarsePointer();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!coarse) return undefined;
    try {
      if (localStorage.getItem(COACH_KEY)) return undefined;
    } catch {
      /* private mode: show it, it costs one tap */
    }
    setShow(true);
    const t = setTimeout(() => setShow(false), 9000);
    return () => clearTimeout(t);
  }, [coarse]);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(COACH_KEY, '1');
    } catch {
      /* nothing to remember it with — it will show once more */
    }
  };

  if (!coarse || !show) return null;
  return (
    <div className="touch-coach" onPointerDown={dismiss} role="button" tabIndex={0} aria-label="Got it">
      <div className="touch-coach-bubble touch-coach-bubble--left">
        Left thumb walks · push far to run · ⤴️ jumps
      </div>
      <div className="touch-coach-bubble touch-coach-bubble--right">
        Right thumb looks around · 🪂 opens the wing in a fall
      </div>
    </div>
  );
}
